import { compare } from "bcrypt-ts";
import {
  createCredentialPackage,
  createLocalSession,
  createVesselInstallation,
} from "../../domain/entities";
import { createUuid } from "../../domain/utils/id";
import { nowIso } from "../../domain/utils/timestamps";
import { oceanLogixDexieDb, OceanLogixDexieDb } from "../../data/local/db";
import { runLocalTransaction } from "../../data/local/db/runLocalTransaction";
import { TABLES } from "../../data/local/db/schema";
import { createLocalDataAccess } from "../../data/local";
import { getCompanyInformation } from "../../utils/GetCompanyInformation";

const LOGIN_THROTTLE_STORAGE_KEY = "localLoginThrottle";
const CURRENT_USER_STORAGE_KEY = "currentUser";
const LEGACY_USERS_STORAGE_KEY = "users";
const SESSION_INACTIVITY_TIMEOUT_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_LOGIN_BACKOFF_MS = 5 * 60 * 1000;

const getThrottleState = () => {
  try {
    return JSON.parse(localStorage.getItem(LOGIN_THROTTLE_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const setThrottleState = (state) => {
  localStorage.setItem(LOGIN_THROTTLE_STORAGE_KEY, JSON.stringify(state));
};

const getThrottleKey = (vesselId, username) =>
  `${String(vesselId || "").trim()}::${String(username || "")
    .trim()
    .toLowerCase()}`;

const mapCredentialPackageUser = (user) => {
  const roles = Array.isArray(user.roles)
    ? user.roles
    : user.role
      ? [user.role]
      : [];
  const displayName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return {
    userId: user.id,
    displayName: displayName || user.userName || user.username || user.id,
    username: user.userName || user.username || user.id,
    role: roles[0] || "user",
    roles,
    passwordVerifier: user.password,
    status: user.status || "active",
  };
};

const mapSessionToUser = (session) => {
  if (!session) return null;

  return {
    username: session.username,
    firstName: session.displayName,
    lastName: "",
    email: "",
    roles: session.roles || [session.role].filter(Boolean),
    userid: session.userId,
    shipId: session.vesselId,
    vesselId: session.vesselId,
    credentialPackageVersion: session.credentialPackageVersion,
    authenticatedAt: session.authenticatedAt,
    lastActivityAt: session.lastActivityAt,
    localSessionId: session.id,
  };
};

const isSessionExpired = (session) => {
  if (!session?.lastActivityAt) return false;
  const lastActivity = new Date(session.lastActivityAt);
  if (Number.isNaN(lastActivity.getTime())) return false;
  return Date.now() - lastActivity.getTime() > SESSION_INACTIVITY_TIMEOUT_MS;
};

const clearLegacySessionMirror = () => {
  localStorage.removeItem(CURRENT_USER_STORAGE_KEY);
};

const persistLegacySessionMirror = (session) => {
  localStorage.setItem(
    CURRENT_USER_STORAGE_KEY,
    JSON.stringify(mapSessionToUser(session))
  );
};

const replaceLegacyUsersMirror = (credentialPackage) => {
  const users = (credentialPackage?.users || []).map((user) => ({
    id: user.userId,
    userName: user.username,
    firstName: user.displayName,
    lastName: "",
    email: "",
    roles: user.roles,
    password: user.passwordVerifier,
    status: user.status,
  }));
  localStorage.setItem(LEGACY_USERS_STORAGE_KEY, JSON.stringify(users));
  return users;
};

const registerFailedLoginAttempt = ({ vesselId, username }) => {
  const throttleState = getThrottleState();
  const key = getThrottleKey(vesselId, username);
  const current = throttleState[key] || { failedAttempts: 0, blockedUntil: null };
  const failedAttempts = current.failedAttempts + 1;
  const delayMs = Math.min(1000 * 2 ** (failedAttempts - 1), MAX_LOGIN_BACKOFF_MS);
  throttleState[key] = {
    failedAttempts,
    blockedUntil: Date.now() + delayMs,
  };
  setThrottleState(throttleState);
};

const resetLoginThrottle = ({ vesselId, username }) => {
  const throttleState = getThrottleState();
  delete throttleState[getThrottleKey(vesselId, username)];
  setThrottleState(throttleState);
};

const assertLoginAllowed = ({ vesselId, username }) => {
  const throttleState = getThrottleState();
  const entry = throttleState[getThrottleKey(vesselId, username)];
  if (!entry?.blockedUntil) return;
  if (Date.now() >= entry.blockedUntil) return;

  const waitSeconds = Math.ceil((entry.blockedUntil - Date.now()) / 1000);
  throw new Error(
    `Too many failed login attempts. Try again in ${waitSeconds} seconds.`
  );
};

const buildCredentialPackageActivationState = async ({
  vesselId,
  users,
  syncedAt,
  vesselInstallationId,
  credentialPackageRepository,
  vesselInstallationRepository,
  resolveCompanyInformation,
}) => {
  const previousActivePackage =
    await credentialPackageRepository.getActiveByVesselId(vesselId);
  const nextVersion =
    (await credentialPackageRepository.getLatestVersionByVesselId(vesselId)) + 1;
  const credentialPackage = createCredentialPackage({
    id: `${vesselId}:${nextVersion}`,
    vesselId,
    version: nextVersion,
    issuedAt: syncedAt,
    activatedAt: syncedAt,
    syncedAt,
    isActive: true,
    users: users.map(mapCredentialPackageUser),
  });
  const existingInstallation =
    await vesselInstallationRepository.getByVesselId(vesselId);
  const { companyName } = resolveCompanyInformation();
  const vesselInstallation = createVesselInstallation({
    id: vesselInstallationId || existingInstallation?.id,
    vesselId,
    tenant: companyName,
    status: "active",
    authMode: "transitional-firebase-project",
    createdAt: existingInstallation?.createdAt,
    lastProvisionedAt: syncedAt,
    updatedAt: syncedAt,
  });

  return {
    credentialPackage,
    vesselInstallation,
    previousActivePackage,
  };
};

export const createLocalAuthService = ({
  db = oceanLogixDexieDb,
  localDataAccess = createLocalDataAccess(db),
  resolveCompanyInformation = getCompanyInformation,
  verifyPassword = compare,
} = {}) => ({
  async activateCredentialPackageWithinTransaction({
    vesselId,
    users,
    syncedAt = nowIso(),
    vesselInstallationId,
    txDb = db,
  }) {
    const credentialPackageRepository =
      localDataAccess.repositories.credentialPackages;
    const vesselInstallationRepository =
      localDataAccess.repositories.vesselInstallations;
    const {
      credentialPackage,
      vesselInstallation,
      previousActivePackage,
    } = await buildCredentialPackageActivationState({
      vesselId,
      users,
      syncedAt,
      vesselInstallationId,
      credentialPackageRepository,
      vesselInstallationRepository,
      resolveCompanyInformation,
    });

    if (previousActivePackage) {
      await txDb.credentialPackages.put({
        ...previousActivePackage,
        isActive: false,
      });
    }

    await txDb.credentialPackages.put(credentialPackage);
    await txDb.vesselInstallations.put(vesselInstallation);

    return {
      credentialPackage,
      vesselInstallation,
    };
  },

  async activateCredentialPackage({
    vesselId,
    users,
    syncedAt = nowIso(),
    vesselInstallationId,
  }) {
    const { credentialPackage } = await runLocalTransaction(
      db,
      () =>
        this.activateCredentialPackageWithinTransaction({
          vesselId,
          users,
          syncedAt,
          vesselInstallationId,
          txDb: db,
        }),
      [TABLES.CREDENTIAL_PACKAGES, TABLES.VESSEL_INSTALLATIONS]
    );

    const mirroredUsers = replaceLegacyUsersMirror(credentialPackage);

    return {
      credentialPackage,
      mirroredUsers,
    };
  },

  async getActiveCredentialPackage(vesselId) {
    if (!vesselId) return null;
    return localDataAccess.repositories.credentialPackages.getActiveByVesselId(
      vesselId
    );
  },

  async authenticateLocally({ vesselId, username, password }) {
    assertLoginAllowed({ vesselId, username });

    const credentialPackage = await this.getActiveCredentialPackage(vesselId);
    if (!credentialPackage) {
      throw new Error("No active credential package found for this vessel.");
    }

    const normalizedUsername = String(username || "").trim().toLowerCase();
    const packageUser = credentialPackage.users.find(
      (user) => user.username.toLowerCase() === normalizedUsername
    );

    if (!packageUser) {
      registerFailedLoginAttempt({ vesselId, username });
      throw new Error("User not found.");
    }

    const isPasswordCorrect = await verifyPassword(
      password,
      packageUser.passwordVerifier
    );

    if (!isPasswordCorrect) {
      registerFailedLoginAttempt({ vesselId, username });
      throw new Error("Incorrect password.");
    }

    resetLoginThrottle({ vesselId, username });

    const authenticatedAt = nowIso();
    const session = createLocalSession({
      id: createUuid(),
      userId: packageUser.userId,
      username: packageUser.username,
      displayName: packageUser.displayName,
      vesselId,
      role: packageUser.role,
      roles: packageUser.roles,
      credentialPackageVersion: credentialPackage.version,
      authenticatedAt,
      lastActivityAt: authenticatedAt,
      status: "active",
    });

    const sessionRepository = localDataAccess.repositories.localSessions;
    const currentActiveSession =
      await sessionRepository.getActiveByVesselId(vesselId);

    await runLocalTransaction(
      db,
      async () => {
        if (currentActiveSession) {
          await db.localSessions.put({
            ...currentActiveSession,
            status: "logged_out",
            lastActivityAt: authenticatedAt,
          });
        }

        await db.localSessions.put(session);
      },
      [TABLES.LOCAL_SESSIONS]
    );

    persistLegacySessionMirror(session);

    return {
      session,
      user: mapSessionToUser(session),
      credentialPackage,
    };
  },

  async resolveCurrentSession(vesselId) {
    if (!vesselId) return null;
    const session =
      await localDataAccess.repositories.localSessions.getActiveByVesselId(vesselId);
    if (!session) {
      clearLegacySessionMirror();
      return null;
    }

    if (isSessionExpired(session)) {
      await db.localSessions.put({
        ...session,
        status: "timed_out",
        lastActivityAt: nowIso(),
      });
      clearLegacySessionMirror();
      return null;
    }

    persistLegacySessionMirror(session);
    return {
      session,
      user: mapSessionToUser(session),
    };
  },

  async touchSession(sessionId) {
    if (!sessionId) return null;
    const session = await db.localSessions.get(sessionId);
    if (!session || session.status !== "active") return null;

    const updatedSession = {
      ...session,
      lastActivityAt: nowIso(),
    };
    await db.localSessions.put(updatedSession);
    persistLegacySessionMirror(updatedSession);
    return updatedSession;
  },

  async logout(vesselId) {
    if (!vesselId) {
      clearLegacySessionMirror();
      return;
    }

    const activeSession =
      await localDataAccess.repositories.localSessions.getActiveByVesselId(vesselId);
    if (activeSession) {
      await db.localSessions.put({
        ...activeSession,
        status: "logged_out",
        lastActivityAt: nowIso(),
      });
    }

    clearLegacySessionMirror();
  },
});

export const localAuthService = createLocalAuthService();

export { OceanLogixDexieDb };
